import { useMemo, useState } from 'react';

import { useLoggedInUserRoles } from '~/components/Auth';
import { useModulesConfig } from '~/data/company';
import { Modules } from '~/data/modules';
import { useSelectedEmployeeId } from '~/hooks/employee/useSelectedEmployeeId';
import { isRoleHR, isRoleManager } from '~/services/Permissions';
import { Optional } from '~/types';

export type UseModulePageFooterOutput = {
  employeeId: number;
  isManager: boolean;
  showSolaIntelShortcut: boolean;
  anchorEl: Optional<HTMLButtonElement>;
  handleSolaIntelOpen: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleSolaIntelClose: () => void;
  openSolaIntel: boolean;
};

export const useModulePageFooter = (): UseModulePageFooterOutput => {
  const { isModuleEnabled } = useModulesConfig();
  const userRoles = useLoggedInUserRoles();
  const { selectedEmployeeId } = useSelectedEmployeeId();
  const isManager = useMemo(() => isRoleManager(userRoles), [userRoles]);
  const showSolaIntelShortcut = useMemo(
    () =>
      isModuleEnabled(Modules.SOLAINTEL) && (isRoleHR(userRoles) || isManager),
    [isModuleEnabled, isManager, userRoles],
  );

  const [anchorEl, setAnchorEl] =
    useState<Optional<HTMLButtonElement>>(undefined);
  const openSolaIntel = Boolean(anchorEl);

  const handleSolaIntelOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSolaIntelClose = () => {
    setAnchorEl(undefined);
  };

  return {
    employeeId: selectedEmployeeId,
    isManager,
    showSolaIntelShortcut,
    anchorEl,
    handleSolaIntelOpen,
    handleSolaIntelClose,
    openSolaIntel,
  };
};
